import React, { useEffect, useMemo, useState } from 'react'
import { Checkbox, Drawer, Dropdown, Menu, Tabs } from 'antd'
import { useSnackbar } from 'notistack'
import Select from 'react-select'
import { AiOutlineDown } from 'react-icons/ai'
import { useHistory, useLocation } from 'react-router-dom'

import { usePet, useToggle } from '../../modules'

import Datepicker from "../reusable/datePicker"
import PatientScheme from './patientScheme'

const { TabPane } = Tabs

const identificationOptions = [
    'Temporary ID',
    'Alien ID',
    'Refugee ID',
    'Mandate Number',
    'Birth Certificate',
    'National ID',
    'Birth Notification',
    'ClientRegistry ID',
].map(item => ({ label: item, value: item }))

const genderOptions = ['Male', 'Female'].map(item => ({ label: item, value: item }))

const NewPatient = ({ onPerform }) => {
    const [name, setName] = useState()
    const [email, setEmail] = useState()
    const [phone, setPhone] = useState()
    const [gender, setGender] = useState()
    const [dob, setDOB] = useState()
    const [age, setAge] = useState()
    const [countries, setCountries] = useState([])
    const [regions, setRegions] = useState([])
    const [locality, setLocality] = useState()
    const [nokName, setNokName] = useState()
    const [nokPhone, setNokPhone] = useState()
    const [nokRShip, setNokRShip] = useState()
    const [broughtByName, setBroughtByName] = useState()
    const [broughtByRShip, setBroughtByRShip] = useState()
    const [broughtByPhone, setBroughtByPhone] = useState()
    const [beneficiaryCrId, setBeneficiaryCrId] = useState()
    const [documentType, setDocType] = useState({ label: 'National ID', value: 'National ID' })
    const [identification, setID] = useState()
    const [country, setCountry] = useState({ label: 'Kenya', value: 'Kenya' })
    const [region, setRegion] = useState()
    const [index, setIndex] = useState()
    const [selected, setSelected] = useState()
    const [schemes, setSchemes] = useState([])
    const [tab, setTab] = useState("0")
    const [registryVerified, setRegistryVerified] = useState(false)
    const [lookupAttempted, setLookupAttempted] = useState(false)
    const [lockedFields, setLockedFields] = useState([])
    const [existingPatient, setExistingPatient] = useState(null)
    const [registryLookupState, setRegistryLookupState] = useState('idle')

    const [pScheme, togglePscheme] = useToggle()
    const [birthday, toggleBirthday] = useToggle(true)
    const [visible, toggleVisible] = useToggle(true)

    const history = useHistory()
    const location = useLocation()
    const { enqueueSnackbar } = useSnackbar()

    const isKenya = String(country?.value ?? '').trim().toLowerCase() === 'kenya'
    const hasIdentificationSeed = Boolean(documentType?.value && String(identification ?? '').trim().length >= 3)
    const registryLookupRequired = isKenya && hasIdentificationSeed

    const lockSet = useMemo(() => new Set(lockedFields), [lockedFields])
    const fieldLocked = (field) => registryVerified && lockSet.has(field)

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 100)
    }

    const patientRouteBase = useMemo(() => {
        if (location.pathname.startsWith('/app/cashier')) return '/app/cashier/patients'
        if (location.pathname.startsWith('/app/front-desk')) return '/app/front-desk/patients'
        if (location.pathname.startsWith('/app/inpatient')) return '/app/inpatient/admissions/patients'
        return '/app/front-desk/patients'
    }, [location.pathname])

    const openExistingPatient = () => {
        if (!existingPatient?._id) return
        toggleVisible()
        setTimeout(() => history.push(`${patientRouteBase}/${existingPatient._id}`), 100)
    }

    const resetRegistryLookup = () => {
        setRegistryVerified(false)
        setLookupAttempted(false)
        setLockedFields([])
        setExistingPatient(null)
        setRegistryLookupState('idle')
    }

    const applyRegistryPatient = (payload) => {
        const patient = payload?.patient ?? {}
        const nextLockedFields = payload?.lockedFields ?? []

        if (patient?.name) setName(patient.name)
        if (patient?.gender) setGender({ label: patient.gender, value: patient.gender })
        if (patient?.dob) {
            setDOB(new Date(patient.dob))
            if (!birthday) toggleBirthday()
            setAge()
        }
        if (patient?.email) setEmail(patient.email)
        if (patient?.phone) setPhone(patient.phone)
        if (patient?.beneficiaryCrId) setBeneficiaryCrId(patient.beneficiaryCrId)
        if (patient?.documentType) setDocType({ label: patient.documentType, value: patient.documentType })
        if (patient?.identification) setID(patient.identification)
        if (patient?.country) setCountry({ label: patient.country, value: patient.country })
        if (patient?.region) setRegion({ label: patient.region, value: patient.region })
        if (patient?.locality) setLocality(patient.locality)

        setLockedFields(nextLockedFields)
        setRegistryVerified(true)
        setExistingPatient(payload?.existingPatient ?? null)
        setRegistryLookupState(payload?.existingPatient ? 'existing-local' : 'matched')
    }

    const modify = e => {
        switch (e.target.name) {
            case 'name':
                setName(e.target.value)
                break
            case 'email':
                setEmail(e.target.value)
                break
            case 'phone':
                setPhone(e.target.value)
                break
            case 'age':
                setAge(e.target.value)
                break
            case 'locality':
                setLocality(e.target.value)
                break
            case 'nokName':
                setNokName(e.target.value)
                break
            case 'nokPhone':
                setNokPhone(e.target.value)
                break
            case 'nokRShip':
                setNokRShip(e.target.value)
                break
            case 'broughtByName':
                setBroughtByName(e.target.value)
                break
            case 'broughtByRShip':
                setBroughtByRShip(e.target.value)
                break
            case 'broughtByPhone':
                setBroughtByPhone(e.target.value)
                break
            case 'identification':
                setID(e.target.value)
                resetRegistryLookup()
                break
            case 'beneficiaryCrId':
                setBeneficiaryCrId(e.target.value)
                break
            default:
                break
        }
    }

    const changeBirthday = () => {
        if (fieldLocked('dob')) return
        toggleBirthday()
        setDOB()
        setAge(0)
    }

    const onComplete = ({ data }, notif, ops) => {
        onPerform(data)
        notif('Patient has been added', ops('success'))
        setTimeout(close, 300)
    }

    const [{ loading: cLoading }] = usePet('countries', null, { notify: true, callback: ({ data }) => setCountries(data) })

    const [{ loading: rLoading }, fetchRegions] = usePet('regions', { name: country?.label }, {
        notify: true,
        manual: true,
        callback: ({ data }) => setRegions(data),
    })

    usePet('schemes', { string: "Cash", active: true, limit: 1 }, {
        notify: true,
        callback: async ({ data }) => {
            if (data?.schemes?.length > 0) {
                setSchemes((current) => current?.length > 0 ? current : [{
                    scheme: { label: data.schemes[0].name, value: data.schemes[0]._id, ...data.schemes[0] },
                    schemeNo: 'Cash',
                    claimantName: 'Patient',
                    main: true
                }])
            }
        }
    })

    const [{ loading: registryLoading, error: registryError }, lookupRegistryPatient] = usePet('lookupRegistryPatient', {
        documentType: documentType?.value,
        identification,
    }, {
        notify: true,
        manual: true,
        callback: ({ data }, notif, ops) => {
            if (data?.notFound) {
                setRegistryVerified(false)
                setLockedFields([])
                setExistingPatient(null)
                setRegistryLookupState('not-found')
                notif(data?.message ?? 'Patient was not found in the registry', ops('warning'))
                return
            }
            applyRegistryPatient(data)
            notif('Registry patient details loaded', ops('success'))
        },
        load: { msg: 'Searching patient registry...' }
    })

    const [{ loading }, addPatient] = usePet('newPatient', {
        name,
        gender: gender?.value,
        dob,
        age,
        email,
        phone,
        country: country?.value,
        region: region?.value,
        locality,
        nokName,
        nokRShip,
        nokPhone,
        beneficiaryCrId,
        documentType: documentType?.value,
        identification,
        registryVerified,
        schemes: schemes?.map(({ scheme, schemeNo, claimantName, utilizedAmount, balance, main }) => ({
            scheme: scheme?.value,
            schemeNo,
            claimantName,
            utilizedAmount,
            balance,
            main
        }))
    }, { notify: true, callback: onComplete, manual: true })

    useEffect(() => {
        if (!country?.label) return
        fetchRegions()
    }, [country?.label])

    useEffect(() => {
        if (!isKenya) resetRegistryLookup()
    }, [isKenya])

    useEffect(() => {
        if (!registryError) return
        setRegistryVerified(false)
        setLockedFields([])
        setExistingPatient(null)
        setRegistryLookupState('error')
    }, [registryError])

    const changeCountry = val => {
        setCountry(val)
        setRegion()
    }

    const runRegistryLookup = () => {
        if (!documentType?.value || !String(identification ?? '').trim()) {
            enqueueSnackbar('Enter the patient identification type and number first')
            return
        }

        setLookupAttempted(true)
        lookupRegistryPatient()
    }

    const changeDocumentType = (val) => {
        setDocType(val)
        resetRegistryLookup()
    }

    const newScheme = () => {
        togglePscheme()
    }

    const savePscheme = val => {
        let temp = [...schemes]

        if (val.main) {
            let prevIndex = temp.findIndex(scheme => scheme?.main)
            if (prevIndex >= 0) temp[prevIndex].main = false
        } else if (temp[index]?.main && !val.main && temp.length > 0) {
            temp[0].main = true
        }

        if (index >= 0) temp[index] = val
        else temp.push(val)

        setSchemes(temp)
    }

    const viewScheme = (val, i) => {
        setSelected(val)
        setIndex(i)
        togglePscheme()
    }

    const removeScheme = i => {
        let temp = [...schemes]
        if (temp[i]?.main && temp.length > 1) temp[0].main = true
        temp = temp.filter((_, k) => i !== k)
        setSchemes(temp)
    }

    const submitDisabled = loading || registryLoading || Boolean(existingPatient) || (registryLookupRequired && !lookupAttempted)

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button disabled={submitDisabled} onClick={addPatient} className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                        Add New Patient
                    </button>
                </div>
            }
            width={600}>
            {
                pScheme &&
                <PatientScheme
                    name={name}
                    toggle={togglePscheme}
                    visible={pScheme}
                    onSave={savePscheme}
                    scheme={selected} />
            }
            <p className="font-extrabold text-3xl mb-0">New Patient</p>
            <Tabs onChange={setTab} activeKey={tab} className="min-h-full">
                <TabPane key="0" tab="Patient Details">
                    <div className="divide-y divide-light-gray-400">
                        <div className="pb-4">
                            <p className="font-bold my-3">Registry Lookup</p>
                            <div className="rounded-sm border border-blue-200 bg-blue-50 px-4 py-4">
                                <p className="text-sm text-blue-800 mb-3">
                                    For Kenya patients, search by identification first. When the registry finds a match, core identity fields are prefilled and locked.
                                </p>
                                <div className="flex flex-wrap -mx-3 mb-3">
                                    <div className="w-full md:w-1/2 px-3 mb-3 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                            Identification Type
                                        </label>
                                        <Select
                                            value={documentType}
                                            onChange={changeDocumentType}
                                            options={identificationOptions}
                                            placeholder="Select ID Type" />
                                    </div>
                                    <div className="w-full md:w-1/2 px-3">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                            Identification No.
                                        </label>
                                        <input
                                            onChange={modify}
                                            className="appearance-none block w-full bg-white text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                            type="text"
                                            value={identification ?? ''}
                                            name="identification"
                                            placeholder="34XXXXXX" />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                        <p className="text-xs text-gray-500 mb-0">Country: {country?.value ?? 'Not selected'}</p>
                                        {
                                            registryVerified &&
                                            <p className="text-xs text-green-700 mb-0 mt-1">Registry verified. Core identity fields are locked below.</p>
                                        }
                                        {
                                            !isKenya &&
                                            <p className="text-xs text-blue-700 mb-0 mt-1">Registry lookup is only required for Kenya. Foreign patients can continue with manual registration.</p>
                                        }
                                        {
                                            isKenya && String(documentType?.value ?? '').trim().toLowerCase() === 'birth notification' &&
                                            <p className="text-xs text-blue-700 mb-0 mt-1">Newborns may not yet exist in the registry. If no match is found, continue manually and update the record later when formal identifiers are available.</p>
                                        }
                                        {
                                            !registryVerified && registryLookupRequired && !lookupAttempted &&
                                            <p className="text-xs text-yellow-700 mb-0 mt-1">Search the registry before saving this Kenya patient.</p>
                                        }
                                        {
                                            registryLookupState === 'not-found' &&
                                            <p className="text-xs text-yellow-700 mb-0 mt-1">No registry match was found. If this is a foreigner, newborn, or a patient not yet registered upstream, you can continue manually.</p>
                                        }
                                        {
                                            registryLookupState === 'error' &&
                                            <p className="text-xs text-yellow-700 mb-0 mt-1">The registry could not be reached right now. Retry the lookup or continue manually only if your operational policy allows it.</p>
                                        }
                                        {
                                            existingPatient &&
                                            <div className="mt-2 rounded-sm border border-red-200 bg-red-50 px-3 py-3">
                                                <p className="text-xs text-red-700 mb-2">
                                                    This patient already exists locally as {existingPatient?.name} ({existingPatient?.opNo ?? existingPatient?.beneficiaryCrId ?? 'existing record'}).
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={openExistingPatient}
                                                        className="btn bg-white border border-red-200 text-red-700 hover:bg-red-100">
                                                        Open Existing Patient
                                                    </button>
                                                    <button
                                                        onClick={resetRegistryLookup}
                                                        className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                                        Search Again
                                                    </button>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    <button
                                        disabled={!isKenya || registryLoading || !hasIdentificationSeed}
                                        onClick={runRegistryLookup}
                                        className="btn hisani-btn-green-light text-white">
                                        {registryLoading ? 'Searching...' : 'Search Registry'}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div>
                                <div>
                                    <p className="font-bold my-3">Basic Information</p>
                                    <div className="flex flex-wrap -mx-3 mb-3">
                                        <div className="w-full md:w-3/5 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Name
                                            </label>
                                            <input
                                                onChange={modify}
                                                disabled={fieldLocked('name')}
                                                className="appearance-none block w-full bg-gray-200 disabled:bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={name ?? ''}
                                                name="name"
                                                placeholder="Evans Kimani" />
                                        </div>
                                        <div className="w-full md:w-2/5 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Gender
                                            </label>
                                            <Select
                                                value={gender}
                                                onChange={setGender}
                                                isDisabled={fieldLocked('gender')}
                                                options={genderOptions}
                                                placeholder="Select Gender" />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-3">
                                        <div className="w-full md:w-2/3 px-3 mb-3">
                                            <Checkbox checked={birthday} disabled={fieldLocked('dob')} onChange={changeBirthday}>
                                                Patient Knows Birth Information
                                            </Checkbox>
                                            {
                                                birthday ?
                                                    <>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                            Date of Birth
                                                        </label>
                                                        <Datepicker value={dob} onChange={fieldLocked('dob') ? () => null : setDOB} />
                                                    </>
                                                    :
                                                    <>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                            Age
                                                        </label>
                                                        <input
                                                            onChange={modify}
                                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                            type="number"
                                                            value={age ?? ''}
                                                            name="age"
                                                            placeholder="Age in years" />
                                                    </>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="font-bold my-3">Identification Information</p>
                                    <div className="flex flex-wrap -mx-3 mb-3">
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Identification Type
                                            </label>
                                            <Select
                                                value={documentType}
                                                onChange={changeDocumentType}
                                                isDisabled={fieldLocked('documentType')}
                                                options={identificationOptions}
                                                placeholder="Select ID Type" />
                                        </div>
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Identification No.
                                            </label>
                                            <input
                                                onChange={modify}
                                                disabled={fieldLocked('identification')}
                                                className="appearance-none block w-full bg-gray-200 disabled:bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={identification ?? ''}
                                                name="identification"
                                                placeholder="34XXXXXX" />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-3 mb-3">
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Beneficiary CR ID <span className="text-gray-500 text-xs normal-case italic">(Optional)</span>
                                            </label>
                                            <input
                                                onChange={modify}
                                                disabled={fieldLocked('beneficiaryCrId')}
                                                className="appearance-none block w-full bg-gray-200 disabled:bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={beneficiaryCrId ?? ''}
                                                name="beneficiaryCrId"
                                                placeholder="CR-XXXXXXXX" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="font-bold my-3">Contact Information</p>
                                    <div className="flex flex-wrap -mx-3">
                                        <div className="w-full md:w-2/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Email <span className="text-gray-500 text-xs normal-case italic">(Optional)</span>
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="email"
                                                value={email ?? ''}
                                                name="email"
                                                placeholder="user@email.com" />
                                        </div>
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Phone
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={phone ?? ''}
                                                name="phone"
                                                placeholder="07XXXXXXXX" />
                                        </div>
                                    </div>
                                    <p className="font-bold my-3">Residential Information</p>
                                    <div className="flex flex-wrap -mx-3 mb-3">
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Country
                                            </label>
                                            <Select
                                                isLoading={cLoading}
                                                value={country}
                                                isDisabled={fieldLocked('country')}
                                                onChange={changeCountry}
                                                options={countries.map(item => ({ label: item, value: item }))}
                                                placeholder="Select Country" />
                                        </div>
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Region
                                            </label>
                                            <Select
                                                isLoading={rLoading}
                                                value={region}
                                                onChange={setRegion}
                                                options={regions.map(item => ({ label: item, value: item }))}
                                                placeholder="Select Region" />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-3 mb-3">
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Locality
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={locality ?? ''}
                                                name="locality"
                                                placeholder="Kamangu" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="font-bold my-3">Next of Kin Information</p>
                                    <div className="flex flex-wrap -mx-3">
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Name
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={nokName ?? ''}
                                                name="nokName"
                                                placeholder="Kamau Samuel" />
                                        </div>
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Relationship
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={nokRShip ?? ''}
                                                name="nokRShip"
                                                placeholder="Relationship" />
                                        </div>
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Phone
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={nokPhone ?? ''}
                                                name="nokPhone"
                                                placeholder="07XXXXXXXX" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="font-bold my-3">Brought By</p>
                                    <div className="flex flex-wrap -mx-3">
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Name
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={broughtByName ?? ''}
                                                name="broughtByName"
                                                placeholder="Wilson Kimani" />
                                        </div>
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Relationship
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={broughtByRShip ?? ''}
                                                name="broughtByRShip"
                                                placeholder="Relationship" />
                                        </div>
                                        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Phone No.
                                            </label>
                                            <input
                                                onChange={modify}
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                type="text"
                                                value={broughtByPhone ?? ''}
                                                name="broughtByPhone"
                                                placeholder="07XXXXXX" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabPane>
                <TabPane key="1" tab="Medical Schemes">
                    <div className="flex justify-end items-center mt-1 mb-5">
                        <button onClick={newScheme} className="btn ml-2 py-2.5 hisani-btn-green-light text-white">New Scheme</button>
                    </div>
                    {
                        schemes?.length > 0 ?
                            <table className="table-auto w-full">
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Scheme Name</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Actions</div></th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        schemes.map((scheme, key) =>
                                            <tr key={key}>
                                                <td className="p-2">
                                                    <div className="text-center">{key + 1}.</div>
                                                </td>
                                                <td className="p-2">
                                                    <div>{scheme?.scheme?.name} {scheme?.main && '(Main)'}</div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    {
                                                        scheme?.scheme?.name !== 'Cash' &&
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Menu.Item onClick={() => viewScheme(scheme, key)}>Edit Scheme</Menu.Item>
                                                                    <Menu.Item onClick={() => removeScheme(key)}>Remove Scheme</Menu.Item>
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">Please click above to add a new scheme</p>
                            </div>
                    }
                </TabPane>
            </Tabs>
        </Drawer>
    )
}

export default NewPatient
